// ABOUTME: Authentication hook that wraps Firebase Auth state changes.
// ABOUTME: One module-level listener feeds the Zustand store; the hook is a pure selector.

import { useCallback, useEffect } from "react";
import type { User } from "firebase/auth";
import { authService, firebaseConfigValid } from "../services/firebase";
import { isBetaApproved } from "../services/firebase/betaCheck";
import { isAdminUser } from "../services/firebase/admin";
import { limitsService } from "../services/limitsService";
import { readAuthGate, writeAuthGate, clearAuthGate } from "../services/authCache";
import { useAuthStore } from "../stores/auth";

const AUTH_TIMEOUT_MS = 5000;

// The listener is registered once for the whole app rather than once per
// useAuth() call site. There are eight of them, three of which are live on the
// dashboard, and each used to run the admin + beta gate independently — 6-9
// Firestore reads per login instead of 2.
let listenerStarted = false;
let lastUserId: string | null = null;
// Denying a user signs them out, which re-enters the listener with a null user.
// Without this flag that pass would clear betaDenied and swallow the
// "Not Yet Approved" message the sign-out was meant to explain.
let denialInFlight = false;

function applyGate(isAdmin: boolean) {
  useAuthStore.getState().setAdmin(isAdmin);
  limitsService.setAdminStatus(isAdmin);
}

/** Runs both gate checks concurrently — neither depends on the other's result. */
async function resolveGate(user: User): Promise<{ isAdmin: boolean; betaApproved: boolean }> {
  const email = user.email ?? undefined;
  const [adminResult, betaResult] = await Promise.allSettled([
    isAdminUser(user.uid, email),
    email ? isBetaApproved(email) : Promise.resolve(false),
  ]);

  if (adminResult.status === "rejected") {
    console.warn("Admin check failed during auth, treating as non-admin:", adminResult.reason);
  }
  const isAdmin = adminResult.status === "fulfilled" && adminResult.value;

  // A failed beta lookup must not lock out a legitimate user, so treat an
  // errored check as approved and let Firestore rules do the real gating.
  const betaApproved = isAdmin || betaResult.status === "rejected" || betaResult.value === true;

  return { isAdmin, betaApproved };
}

async function denyBeta() {
  denialInFlight = true;
  useAuthStore.getState().setBetaDenied(true);
  await authService.signOut();
}

/** Re-checks a cache-admitted user against Firestore and corrects course. */
async function revalidate(user: User) {
  try {
    const gate = await resolveGate(user);
    writeAuthGate(user.uid, gate);
    if (useAuthStore.getState().user?.uid !== user.uid) return; // switched users mid-flight
    applyGate(gate.isAdmin);
    if (!gate.betaApproved) {
      clearAuthGate(user.uid);
      await denyBeta();
    }
  } catch (err) {
    console.warn("Background auth revalidation failed:", err);
  }
}

function startAuthListener() {
  if (listenerStarted) return;
  listenerStarted = true;

  const { setUser, setLoading, setBetaDenied } = useAuthStore.getState();

  // If Firebase config is missing, onAuthStateChanged will never fire — clear
  // loading immediately so the splash page shows instead of a spinner.
  if (!firebaseConfigValid) {
    console.error("Firebase config invalid — skipping auth listener");
    setLoading(false);
    return;
  }

  // Safety net for a listener that never fires (broken config, offline).
  const timeout = setTimeout(() => {
    if (useAuthStore.getState().isLoading) {
      console.warn("Auth state listener timed out — clearing loading state");
      setLoading(false);
    }
  }, AUTH_TIMEOUT_MS);

  authService.onAuthStateChanged(async (newUser) => {
    clearTimeout(timeout);

    if (newUser?.uid === lastUserId) {
      // Same user — almost always a token refresh. Don't re-run the gate.
      if (newUser) setLoading(false);
      return;
    }
    lastUserId = newUser?.uid ?? null;

    if (!newUser) {
      // Preserve betaDenied when this null is the sign-out we just triggered.
      if (denialInFlight) {
        denialInFlight = false;
      } else {
        setBetaDenied(false);
      }
      applyGate(false);
      setUser(null);
      return;
    }

    if (!newUser.email) {
      setUser(newUser);
      return;
    }

    // Fast path: a previous session already cleared this user. Admit them from
    // the local snapshot so the dashboard paints without waiting on Firestore,
    // then confirm in the background.
    const cached = readAuthGate(newUser.uid);
    if (cached?.betaApproved) {
      applyGate(cached.isAdmin);
      setBetaDenied(false);
      setUser(newUser);
      void revalidate(newUser);
      return;
    }

    // Cold path: block on the gate.
    const gate = await resolveGate(newUser);
    writeAuthGate(newUser.uid, gate);
    applyGate(gate.isAdmin);

    if (!gate.betaApproved) {
      clearAuthGate(newUser.uid);
      await denyBeta();
      return;
    }

    setBetaDenied(false);
    setUser(newUser);
  });
}

export function useAuth() {
  const { user, isLoading, isAdmin, betaDenied, setBetaDenied } = useAuthStore();

  useEffect(() => {
    startAuthListener();
  }, []);

  const signOut = useCallback(async () => {
    const currentUserId = useAuthStore.getState().user?.uid;
    try {
      if (currentUserId) {
        clearAuthGate(currentUserId);
      }
      await authService.signOut();
    } catch (err) {
      console.error("Error signing out:", err);
      // Still clear local state so the user isn't stuck.
      useAuthStore.getState().signOut();
    }
  }, []);

  return {
    user,
    loading: isLoading,
    isAdmin,
    signOut,
    betaDenied,
    setBetaDenied,
  };
}
